/**
 * CareRequestService
 * Service for actioning public care/help requests (approve / reject)
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for the admin care request queue: paged list views, detail views and the
 * approve/reject decisions
 */
export class CareRequestService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? CareRequestService.DEFAULT_SERVICE_PATH,
            basePath: CareRequestService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get the paged request list view (filtered)
     * @param filter - Optional filter parameters
     * @returns Paged request list view response
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        if (!response.data) {
            throw new Error('Failed to retrieve care request list view');
        }
        return response.data;
    }
    /**
     * Get the paged request list view (filtered) with full response
     * @param filter - Optional filter parameters
     * @returns ResponseData with paged request list view response
     */
    listViewRaw(filter) {
        const url = this.buildUrl('list-view');
        return this.http.post(url, filter ?? {});
    }
    /**
     * Get a request detail view by ID
     * @param id - The request ID
     * @returns Care request model view
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Care request with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get a request detail view by ID with full response
     * @param id - The request ID
     * @returns ResponseData with care request model view
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    /**
     * Approve a care request (creates the care case)
     * @param id - The request ID
     * @param request - Optional approval payload (leader assignment, zone, note)
     * @returns The created care case
     */
    async approve(id, request) {
        const response = await this.approveRaw(id, request);
        if (!response.data) {
            throw new Error(`Failed to approve care request ${id}`);
        }
        return response.data;
    }
    /**
     * Approve a care request with full response
     * @param id - The request ID
     * @param request - Optional approval payload
     * @returns ResponseData with the created care case
     */
    approveRaw(id, request) {
        const url = this.buildUrl(`${id}/approve`);
        return this.http.post(url, request ?? {});
    }
    /**
     * Reject a care request
     * @param id - The request ID
     * @param request - Rejection payload with reason
     * @returns True if the rejection succeeded
     */
    async reject(id, request) {
        const response = await this.rejectRaw(id, request);
        return response.success;
    }
    /**
     * Reject a care request with full response
     * @param id - The request ID
     * @param request - Rejection payload with reason
     * @returns ResponseData (success/fail)
     */
    rejectRaw(id, request) {
        const url = this.buildUrl(`${id}/reject`);
        return this.http.post(url, request);
    }
    /**
     * Soft-delete a care request off the queue (spam/junk). Refuses (400) an approved request — it has a
     * care case behind it, and deleting would orphan `CareCase.requestID`. Cancelling a genuine withdrawal
     * is a different action (`changeStatus(id, -2)`) and is left untouched. ActsCareAdmin only.
     * DELETE api/care-requests/{id}
     * @param id - The request ID
     * @returns True if the request was removed (false when it was approved or not found)
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    deleteRaw(id) {
        const url = this.buildUrl(`${id}`);
        return this.http.delete(url);
    }
    /**
     * Soft-delete many care requests off the queue in one call. Approved/missing requests are skipped
     * server-side, so this reports partial success honestly. ActsCareAdmin only.
     * POST api/care-requests/delete-many
     * @param ids - The request IDs to delete
     * @returns How many requests were actually removed
     */
    async deleteMany(ids) {
        const response = await this.deleteManyRaw(ids);
        return response.data?.removed ?? 0;
    }
    deleteManyRaw(ids) {
        const url = this.buildUrl('delete-many');
        return this.http.post(url, { ids });
    }
    /**
     * Change only the request's status.
     * PATCH api/care-requests/{id}/change-status/{newStatus}
     *
     * Note: Approve (1) and Reject (-1) are workflow-owned and rejected by the API with 400 —
     * use {@link approve} / {@link reject} for those, so the care case is created properly.
     * @param id - The request ID
     * @param newStatus - The new RequestStatusID
     * @returns The updated care request
     */
    async changeStatus(id, newStatus) {
        const response = await this.changeStatusRaw(id, newStatus);
        if (!response.data) {
            throw new Error(`Failed to change status for care request ${id}`);
        }
        return response.data;
    }
    /**
     * Change only the request's status with full response
     * @param id - The request ID
     * @param newStatus - The new RequestStatusID
     * @returns ResponseData with the updated care request
     */
    changeStatusRaw(id, newStatus) {
        const url = this.buildUrl(`${id}/change-status/${newStatus}`);
        return this.http.patch(url);
    }
    /**
     * Ranked candidate member matches for one counselee (by exact email, phone, or name), so the admin
     * can pick the right person — or 'create new' — before approving. Send the chosen memberIDs back on
     * the approve payload (`subjectMembers`). An empty list = no candidates → create new.
     * POST api/care-requests/candidate-matches
     */
    async candidateMatches(query) {
        const response = await this.candidateMatchesRaw(query);
        return response.data ?? [];
    }
    candidateMatchesRaw(query) {
        const url = this.buildUrl('candidate-matches');
        return this.http.post(url, query);
    }
}
CareRequestService.DEFAULT_SERVICE_PATH = 'ac-counselling';
CareRequestService.BASE_PATH = 'api/care-requests';
//# sourceMappingURL=CareRequestService.js.map